/**
 * Compliance Document Editor Page
 * Full-page editor for creating and editing compliance documents
 * Provides rich text formatting with markdown support
 * Access: super_admin, ops_admin
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Loader2,
  Save,
  Eye,
  FileText,
  Shield,
  AlertTriangle,
  CheckCircle,
  Clock,
} from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';
import { RichTextEditor } from '@/components/ui/rich-text-editor';

interface ConsentDocument {
  id: number;
  document_type: 'terms_of_use' | 'hipaa_authorization';
  version: string;
  version_number: number;
  title: string;
  content: string;
  summary?: string;
  change_description?: string;
  is_active: boolean;
  requires_reconsent: boolean;
  effective_date: string;
}

export default function ComplianceDocumentEditorPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const documentId = searchParams.get('id');
  const isEditing = !!documentId;

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Form state
  const [form, setForm] = useState({
    document_type: 'terms_of_use' as 'terms_of_use' | 'hipaa_authorization',
    version: '',
    version_number: 1.0,
    title: '',
    content: '',
    summary: '',
    change_description: '',
    requires_reconsent: true,
    effective_date: new Date().toISOString().split('T')[0],
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    const cboId = localStorage.getItem('active_cbo_id') || '';
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      'X-CBO-ID': cboId,
    };
  };

  // Load existing document if editing
  useEffect(() => {
    if (documentId) {
      const loadDocument = async () => {
        try {
          const response = await fetch(
            `${getWeaveinApiUrl()}/api/consent/admin/documents`,
            { headers: getAuthHeaders() }
          );
          if (!response.ok) throw new Error('Failed to load document');

          const documents: ConsentDocument[] = await response.json();
          const doc = documents.find(d => d.id === parseInt(documentId));

          if (doc) {
            setForm({
              document_type: doc.document_type,
              version: doc.version,
              version_number: doc.version_number,
              title: doc.title,
              content: doc.content,
              summary: doc.summary || '',
              change_description: doc.change_description || '',
              requires_reconsent: doc.requires_reconsent,
              effective_date: doc.effective_date.split('T')[0],
            });
          }
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to load document');
        } finally {
          setLoading(false);
        }
      };
      loadDocument();
    }
  }, [documentId]);

  // Warn about unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const handleFormChange = (field: string, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
    setSuccess(null);
  };

  const handleSave = async (asDraft: boolean = true) => {
    if (!form.version || !form.title || !form.content) {
      setError('Please fill in all required fields (Version, Title, Content)');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      let response: Response;

      if (isEditing && documentId) {
        // Update existing draft document
        const updatePayload = {
          version: form.version,
          version_number: form.version_number,
          title: form.title,
          content: form.content,
          summary: form.summary || null,
          change_description: form.change_description || null,
          requires_reconsent: form.requires_reconsent,
          effective_date: form.effective_date,
        };

        response = await fetch(
          `${getWeaveinApiUrl()}/api/consent/admin/documents/${documentId}`,
          {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify(updatePayload),
          }
        );
      } else {
        // Create new document
        const createPayload = {
          document_type: form.document_type,
          version: form.version,
          version_number: form.version_number,
          title: form.title,
          content: form.content,
          summary: form.summary || null,
          change_description: form.change_description || null,
          requires_reconsent: form.requires_reconsent,
          is_major_change: form.requires_reconsent,
          effective_date: form.effective_date,
        };

        response = await fetch(
          `${getWeaveinApiUrl()}/api/consent/admin/documents`,
          {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(createPayload),
          }
        );
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save document');
      }

      setHasUnsavedChanges(false);
      setSuccess(isEditing ? 'Document updated successfully' : 'Document saved successfully as draft');

      // Navigate back to list after short delay
      setTimeout(() => {
        navigate('/admin/compliance-documents');
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save document');
    } finally {
      setSaving(false);
    }
  };

  const handleGoBack = () => {
    if (hasUnsavedChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
        navigate('/admin/compliance-documents');
      }
    } else {
      navigate('/admin/compliance-documents');
    }
  };

  const getDocumentTypeLabel = (type: string) => {
    return type === 'terms_of_use' ? 'Terms of Service' : 'HIPAA Authorization';
  };

  const getDocumentTypeIcon = (type: string) => {
    return type === 'terms_of_use' ? FileText : Shield;
  };

  // Simple markdown to HTML converter for preview
  const renderMarkdown = (text: string): string => {
    if (!text) return '';

    let html = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/^### (.+)$/gm, '<h3 class="text-lg font-semibold mt-4 mb-2">$1</h3>')
      .replace(/^## (.+)$/gm, '<h2 class="text-xl font-semibold mt-4 mb-2">$1</h2>')
      .replace(/^# (.+)$/gm, '<h1 class="text-2xl font-bold mt-4 mb-2">$1</h1>')
      .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/^- (.+)$/gm, '<li class="ml-4">$1</li>')
      .replace(/^\d+\. (.+)$/gm, '<li class="ml-4 list-decimal">$1</li>')
      .replace(/\n\n/g, '</p><p class="my-2">')
      .replace(/\n/g, '<br />');

    return `<div class="prose prose-sm max-w-none"><p class="my-2">${html}</p></div>`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  const DocIcon = getDocumentTypeIcon(form.document_type);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={handleGoBack}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  form.document_type === 'terms_of_use'
                    ? 'bg-blue-100 dark:bg-blue-900/30'
                    : 'bg-purple-100 dark:bg-purple-900/30'
                }`}>
                  <DocIcon className={`w-5 h-5 ${
                    form.document_type === 'terms_of_use'
                      ? 'text-blue-600 dark:text-blue-400'
                      : 'text-purple-600 dark:text-purple-400'
                  }`} />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                    {isEditing ? 'Edit Document' : 'Create New Document'}
                  </h1>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {getDocumentTypeLabel(form.document_type)}
                    {form.version && ` • v${form.version}`}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {hasUnsavedChanges && (
                <span className="text-sm text-yellow-600 dark:text-yellow-400 flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  Unsaved changes
                </span>
              )}
              <button
                onClick={() => setShowPreview(!showPreview)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                  showPreview
                    ? 'bg-teal-50 border-teal-300 text-teal-700 dark:bg-teal-900/20 dark:border-teal-700 dark:text-teal-400'
                    : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <Eye className="w-4 h-4" />
                {showPreview ? 'Hide Preview' : 'Show Preview'}
              </button>
              <button
                onClick={() => handleSave(true)}
                disabled={saving || !form.version || !form.title || !form.content}
                className="flex items-center gap-2 px-6 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {isEditing ? 'Update Document' : 'Save Draft'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 pt-4">
          <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-4 rounded-lg flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
            <button onClick={() => setError(null)} className="ml-auto">×</button>
          </div>
        </div>
      )}
      {success && (
        <div className="max-w-7xl mx-auto px-4 pt-4">
          <div className="bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 p-4 rounded-lg flex items-center gap-2">
            <CheckCircle className="w-5 h-5 flex-shrink-0" />
            <span>{success}</span>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className={`grid gap-6 ${showPreview ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'}`}>
          {/* Editor Panel */}
          <div className="space-y-6">
            {/* Document Metadata */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Document Details
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Document Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Document Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={form.document_type}
                    onChange={(e) => handleFormChange('document_type', e.target.value)}
                    disabled={isEditing}
                    className={`w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white ${isEditing ? 'opacity-60 cursor-not-allowed' : ''}`}
                  >
                    <option value="terms_of_use">Terms of Service</option>
                    <option value="hipaa_authorization">HIPAA Authorization</option>
                  </select>
                  {isEditing && (
                    <p className="text-xs text-gray-500 mt-1">Document type cannot be changed when editing</p>
                  )}
                </div>

                {/* Effective Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Effective Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={form.effective_date}
                    onChange={(e) => handleFormChange('effective_date', e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                </div>

                {/* Version */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Version <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.version}
                    onChange={(e) => handleFormChange('version', e.target.value)}
                    placeholder="e.g., 2026.01.30"
                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                </div>

                {/* Version Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Version Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={form.version_number}
                    onChange={(e) => handleFormChange('version_number', parseFloat(e.target.value))}
                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              {/* Title */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => handleFormChange('title', e.target.value)}
                  placeholder="Harmony Healthcare Solutions Terms of Service"
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                />
              </div>

              {/* Summary */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Summary
                </label>
                <input
                  type="text"
                  value={form.summary}
                  onChange={(e) => handleFormChange('summary', e.target.value)}
                  placeholder="Brief summary of document purpose"
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                />
              </div>

              {/* Change Description */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Change Description
                </label>
                <input
                  type="text"
                  value={form.change_description}
                  onChange={(e) => handleFormChange('change_description', e.target.value)}
                  placeholder="What changed from the previous version"
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                />
              </div>

              {/* Requires Re-consent */}
              <div className="mt-4 flex items-center gap-2">
                <input
                  type="checkbox"
                  id="requires_reconsent"
                  checked={form.requires_reconsent}
                  onChange={(e) => handleFormChange('requires_reconsent', e.target.checked)}
                  className="w-4 h-4 text-teal-500 border-gray-300 rounded focus:ring-teal-500"
                />
                <label htmlFor="requires_reconsent" className="text-sm text-gray-700 dark:text-gray-300">
                  Requires users to re-consent when activated
                </label>
              </div>
            </div>

            {/* Document Content */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Document Content <span className="text-red-500">*</span>
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Use Markdown formatting for headings, lists, bold, italic, and more.
              </p>
              <RichTextEditor
                value={form.content}
                onChange={(value) => handleFormChange('content', value)}
                placeholder="Enter your document content here. Use Markdown formatting..."
                minHeight="500px"
              />
            </div>
          </div>

          {/* Preview Panel */}
          {showPreview && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="sticky top-20 bg-gray-50 dark:bg-gray-700 px-6 py-4 border-b border-gray-200 dark:border-gray-600">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Preview
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  This is how the document will appear to users
                </p>
              </div>
              <div className="p-6 max-h-[calc(100vh-200px)] overflow-y-auto">
                {/* Document Header */}
                <div className="mb-6 pb-4 border-b border-gray-200 dark:border-gray-700">
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    {form.title || 'Untitled Document'}
                  </h1>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Version {form.version || '0.0'} • Effective: {form.effective_date}
                  </p>
                </div>

                {/* Summary */}
                {form.summary && (
                  <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      <strong>Summary:</strong> {form.summary}
                    </p>
                  </div>
                )}

                {/* Content */}
                <div
                  className="prose prose-sm dark:prose-invert max-w-none"
                  dangerouslySetInnerHTML={{ __html: renderMarkdown(form.content) }}
                />

                {/* Footer */}
                <div className="mt-8 pt-4 border-t border-gray-200 dark:border-gray-700 text-center">
                  <p className="text-sm text-gray-500">
                    © {new Date().getFullYear()} Harmony Healthcare Solutions, Inc. All rights reserved.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
